import React, { createContext, useContext, ReactNode } from 'react';
import { Libraries, useJsApiLoader } from '@react-google-maps/api';

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError?: Error;
}

interface Props {
  children: ReactNode;
}

const GoogleMapsContext = createContext<GoogleMapsContextType | undefined>(
  undefined
);

const LIBRARIES: Libraries = ['drawing', 'places', 'geometry', 'visualization'];
let hasLoggedMissingApiKey = false;

const GoogleMapsLoader: React.FC<
  Props & { apiKey: string; mapId?: string }
> = ({ children, apiKey, mapId }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
    libraries: LIBRARIES,
    version: '3.64', // Pin to 3.64 to keep DrawingManager support (deprecated in 3.65+) TODO: update it and make custom toolbar
    ...(mapId ? { mapIds: [mapId] } : {}),
  });

  if (loadError) {
    console.error('Error loading Google Maps API:', loadError);
    return (
      <div>
        Error loading Google Maps API. Please check API key, console, and
        library names.
      </div>
    );
  }

  if (!isLoaded) {
    return null;
  }

  return (
    <GoogleMapsContext.Provider value={{ isLoaded, loadError }}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

/**
 * Loads the Maps script (same options as the provider, so both share one load)
 * without holding back or replacing its children. For forms that work without
 * Google and only use Places when it is there.
 */
const QuietLoader: React.FC<{ apiKey: string; mapId?: string }> = ({
  apiKey,
  mapId,
}) => {
  useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
    libraries: LIBRARIES,
    version: '3.64',
    ...(mapId ? { mapIds: [mapId] } : {}),
  });
  return null;
};

export const GoogleMapsScript: React.FC = () => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAP_API_KEY;
  return apiKey ? (
    <QuietLoader apiKey={apiKey} mapId={process.env.REACT_APP_GOOGLE_MAP_ID} />
  ) : null;
};

export const GoogleMapsProvider: React.FC<Props> = ({ children }) => {
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAP_API_KEY;
  const googleMapsMapId = process.env.REACT_APP_GOOGLE_MAP_ID;

  if (!googleMapsApiKey) {
    if (!hasLoggedMissingApiKey) {
      hasLoggedMissingApiKey = true;
      console.warn(
        'Google Maps API key is not configured. Map views will be disabled until REACT_APP_GOOGLE_MAP_API_KEY is set.'
      );
    }
    return (
      <GoogleMapsContext.Provider value={{ isLoaded: false }}>
        {children}
      </GoogleMapsContext.Provider>
    );
  }

  return (
    <GoogleMapsLoader apiKey={googleMapsApiKey} mapId={googleMapsMapId}>
      {children}
    </GoogleMapsLoader>
  );
};

/** Undefined outside a provider (a test asserts it); typed as defined for the callers that always sit inside one. */
export const useGoogleMaps = (): GoogleMapsContextType =>
  useContext(GoogleMapsContext) as GoogleMapsContextType;
